import React, { Fragment, useEffect, useState } from "react";
import {
  Box,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import {
  getTemplatesForProject,
  saveTemplate,
  deleteTemplate,
} from "../../../Services/AllotmentLetterTemplate";
import Button from "../../../Subcomponents/Button";
import ConfirmationAlert from "../../../Subcomponents/ConfirmationAlert";
import cssClasses from "./ProjectDetails.module.css";

const VARIABLES_HINT =
  "Variables: {{unitNo}}, {{areaDifference}}, {{areaDifferenceInSqFt}}, {{floorNo}}, {{wingName}}, {{bsp}}, {{paidAmt}}, {{paidDate}}, {{paidChequeNo}}, {{parkingfloor}}, {{parkingNumber}}, {{parkingLength}}, {{parkingBreadth}}, {{parkingHeight}}";

const labelSx = {
  fontFamily: "Inter-Medium",
  fontWeight: 500,
  fontSize: "1.4rem",
  lineHeight: "2rem",
  color: "#344054",
  marginBottom: "0.6rem",
  display: "block",
};

const hintSx = {
  fontFamily: "Inter-Regular",
  fontSize: "1.2rem",
  color: "#667085",
  marginTop: "0.4rem",
  whiteSpace: "pre-line",
};

const inputSx = {
  "& .MuiInputBase-root": {
    backgroundColor: "#F2F4F7",
    borderRadius: "0.8rem",
    fontSize: "1.4rem",
    fontFamily: "Inter-Regular",
  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "#EAECF0",
  },
  "& .MuiInputBase-input": {
    padding: "1rem 1.2rem",
    fontSize: "1.4rem",
    fontFamily: "Inter-Regular",
    color: "#344054",
  },
};

const selectSx = {
  backgroundColor: "#F2F4F7",
  borderRadius: "0.8rem",
  fontSize: "1.4rem",
  fontFamily: "Inter-Regular",
  color: "#344054",
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#EAECF0" },
  "& .MuiSelect-select": { padding: "1rem 1.2rem", fontSize: "1.4rem", fontFamily: "Inter-Regular" },
};

const menuItemSx = { fontSize: "1.4rem", fontFamily: "Inter-Regular" };

const sectionCardSx = {
  background: "#FCFCFD",
  border: "1px solid #EAECF0",
  borderRadius: "0.8rem",
  padding: "2rem 2.4rem",
  marginBottom: "1.6rem",
};

const sectionTitleSx = {
  fontFamily: "Inter-Medium",
  fontSize: "1.5rem",
  fontWeight: 600,
  color: "#344054",
  marginBottom: "1.6rem",
};

export default function AllotmentLetterTemplateSection({
  projectId,
  allWings = [],
}) {
  const [templates, setTemplates] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);

  useEffect(() => {
    if (projectId) loadTemplates();
  }, [projectId]);

  async function loadTemplates() {
    const data = await getTemplatesForProject(projectId);
    setTemplates(data);
  }

  function startCreate() {
    setEditing({
      name: "",
      project_id: projectId,
      wing_id: "",
      show_condition: "always",
      subject: "",
      section1_paragraph: "",
      has_parking_section: false,
      receipt_paragraph: "",
      possession_date: "",
      possession_includes_parking: false,
      has_annexure_a: false,
      signatory_enabled: false,
      signatory_company: "",
      signatory_name: "",
      signatory_email: "",
      applicant_layout: "standard",
      acknowledgement_show_date: true,
    });
    setError("");
    setSuccess("");
  }

  function startEdit(tmpl) {
    setEditing({ ...tmpl, wing_id: tmpl.wing_id ?? "" });
    setError("");
    setSuccess("");
  }

  function cancelEdit() {
    setEditing(null);
    setError("");
    setSuccess("");
  }

  function handleChange(e) {
    const { name, value, type, checked } = e.target;
    setEditing((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  }

  async function handleSave(e) {
    e.preventDefault();
    setError("");
    setSuccess("");
    if (!editing.name.trim()) {
      setError("Template name is required.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...editing,
        project_id: projectId,
        wing_id: editing.wing_id?.trim() || null,
      };
      await saveTemplate(payload);
      setSuccess("Template saved successfully.");
      await loadTemplates();
      setEditing(null);
    } catch (err) {
      setError("Save failed: " + (err.message || JSON.stringify(err)));
    } finally {
      setSaving(false);
    }
  }

  function handleDelete(id, name) {
    setDeleteTarget({ id, name });
  }

  async function handleConfirmDelete(response) {
    if (response !== "confirmed") {
      setDeleteTarget(null);
      return;
    }
    try {
      await deleteTemplate(deleteTarget.id);
      await loadTemplates();
    } catch (err) {
      alert("Delete failed: " + err.message);
    } finally {
      setDeleteTarget(null);
    }
  }

  if (editing) {
    return (
      <TemplateForm
        template={editing}
        onChange={handleChange}
        onSave={handleSave}
        onCancel={cancelEdit}
        saving={saving}
        error={error}
        success={success}
        allWings={allWings}
      />
    );
  }

  return (
    <Fragment>
      <Box className={cssClasses.SubContainer}>
        <Box className={cssClasses.DivContainer}>
          <Box className={cssClasses.ProjectTitle}>
            Allotment Letter Templates
          </Box>
        </Box>
        <Button
          style={{
            fontSize: "1.4rem",
            lineHeight: "2rem",
            color: "#FFF",
            backgroundColor: "#155EEF",
            borderRadius: "0.8rem",
            padding: "0.8rem 2rem",
          }}
          btnName="+ New Template"
          clicked={startCreate}
        />
      </Box>

      <Box height="100%" overflow="auto">
        <Box className={cssClasses.MainDiv} flexDirection="column !important">
          {templates.length === 0 ? (
            <Typography
              sx={{
                fontFamily: "Inter-Regular",
                fontSize: "1.4rem",
                color: "#667085",
                textAlign: "center",
                padding: "4rem 0",
              }}
            >
              No templates yet for this project. Click "+ New Template" to
              create one.
            </Typography>
          ) : (
            <Box sx={{ overflowX: "auto", width: "100%" }}>
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontSize: "1.3rem",
                  fontFamily: "Inter-Regular",
                }}
              >
                <thead>
                  <tr
                    style={{
                      background: "#F2F4F7",
                      borderBottom: "2px solid #EAECF0",
                    }}
                  >
                    {["Name", "Wing", "Show When", "Parking", "Annexure A", "Actions"].map(
                      (h) => (
                        <th
                          key={h}
                          style={{
                            padding: "1rem 1.2rem",
                            textAlign: "left",
                            fontFamily: "Inter-Medium",
                            fontWeight: 600,
                            fontSize: "1.3rem",
                            color: "#344054",
                          }}
                        >
                          {h}
                        </th>
                      )
                    )}
                  </tr>
                </thead>
                <tbody>
                  {templates.map((tmpl) => {
                    const wing = allWings.find(
                      (w) => w.wingId?.wingId === tmpl.wing_id
                    );
                    return (
                      <tr
                        key={tmpl.id}
                        style={{ borderBottom: "1px solid #EAECF0" }}
                      >
                        <td
                          style={{
                            padding: "1rem 1.2rem",
                            color: "#344054",
                            fontFamily: "Inter-Regular",
                          }}
                        >
                          {tmpl.name}
                        </td>
                        <td
                          style={{
                            padding: "1rem 1.2rem",
                            color: "#344054",
                            fontFamily: "Inter-Regular",
                          }}
                        >
                          {wing
                            ? wing.wingId?.wingName
                            : tmpl.wing_id
                            ? tmpl.wing_id
                            : "— Project Level"}
                        </td>
                        <td
                          style={{
                            padding: "1rem 1.2rem",
                            color: "#344054",
                            fontFamily: "Inter-Regular",
                          }}
                        >
                          {tmpl.show_condition}
                        </td>
                        <td
                          style={{
                            padding: "1rem 1.2rem",
                            color: "#344054",
                            fontFamily: "Inter-Regular",
                          }}
                        >
                          {tmpl.has_parking_section ? "Yes" : "No"}
                        </td>
                        <td
                          style={{
                            padding: "1rem 1.2rem",
                            color: "#344054",
                            fontFamily: "Inter-Regular",
                          }}
                        >
                          {tmpl.has_annexure_a ? "Yes" : "No"}
                        </td>
                        <td style={{ padding: "1rem 1.2rem" }}>
                          <button
                            onClick={() => startEdit(tmpl)}
                            style={{
                              marginRight: "0.8rem",
                              padding: "0.4rem 1.2rem",
                              background: "#EEF4FF",
                              color: "#155EEF",
                              border: "1px solid #C7D7FD",
                              borderRadius: "0.6rem",
                              cursor: "pointer",
                              fontSize: "1.2rem",
                              fontFamily: "Inter-Medium",
                            }}
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDelete(tmpl.id, tmpl.name)}
                            style={{
                              padding: "0.4rem 1.2rem",
                              background: "#FEF3F2",
                              color: "#B42318",
                              border: "1px solid #FECDCA",
                              borderRadius: "0.6rem",
                              cursor: "pointer",
                              fontSize: "1.2rem",
                              fontFamily: "Inter-Medium",
                            }}
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </Box>
          )}
        </Box>
      </Box>

      <ConfirmationAlert
        open={!!deleteTarget}
        close={() => setDeleteTarget(null)}
        title="Delete Template"
        subTitle={`Are you sure you want to delete "${deleteTarget?.name}"? This cannot be undone.`}
        buttonTitle="No"
        returnRespButton="Yes, Delete"
        confirm={handleConfirmDelete}
      />
    </Fragment>
  );
}

function TemplateForm({
  template,
  onChange,
  onSave,
  onCancel,
  saving,
  error,
  success,
  allWings,
}) {
  const isNew = !template.id;

  return (
    <Fragment>
      <Box className={cssClasses.SubContainer}>
        <Box className={cssClasses.DivContainer}>
          <Box className={cssClasses.ProjectTitle}>
            {isNew ? "New Allotment Letter Template" : `Edit: ${template.name}`}
          </Box>
        </Box>
        <Button
          style={{
            fontSize: "1.4rem",
            lineHeight: "2rem",
            color: "#344054",
            backgroundColor: "#FFFFFF",
            borderRadius: "0.8rem",
            padding: "0.8rem 2rem",
            border: "1px solid #D0D5DD",
          }}
          btnName="← Back to List"
          clicked={onCancel}
        />
      </Box>

      {error && (
        <Box
          sx={{
            background: "#FEF3F2",
            color: "#B42318",
            padding: "1rem 1.6rem",
            borderRadius: "0.8rem",
            margin: "0 1rem 1.6rem",
            border: "1px solid #FECDCA",
            fontFamily: "Inter-Regular",
            fontSize: "1.4rem",
          }}
        >
          {error}
        </Box>
      )}
      {success && (
        <Box
          sx={{
            background: "#ECFDF3",
            color: "#027A48",
            padding: "1rem 1.6rem",
            borderRadius: "0.8rem",
            margin: "0 1rem 1.6rem",
            border: "1px solid #A6F4C5",
            fontFamily: "Inter-Regular",
            fontSize: "1.4rem",
          }}
        >
          {success}
        </Box>
      )}

      <Box
        component="form"
        onSubmit={onSave}
        height="100%"
        overflow="auto"
      >
        <Box className={cssClasses.MainDiv} flexDirection="column !important">

          {/* ── Identity ── */}
          <Box sx={sectionCardSx}>
            <Typography sx={sectionTitleSx}>Identity</Typography>
            <Box className={cssClasses.FlexRow22}>
              <Box className={cssClasses.additionalInputContainer} flex="100% !important">
                <FormField label="Template Name *" hint="Used only in this dashboard">
                  <TextField
                    fullWidth
                    size="small"
                    name="name"
                    value={template.name}
                    onChange={onChange}
                    required
                    sx={inputSx}
                  />
                </FormField>
              </Box>
              <Box className={cssClasses.additionalInputContainer} flex="100% !important">
                <FormField label="Wing" hint="Leave blank for a project-level template (applies to all wings). Select a wing to create a wing-specific override.">
                  <Select
                    fullWidth
                    size="small"
                    name="wing_id"
                    value={template.wing_id || ""}
                    onChange={onChange}
                    sx={selectSx}
                    inputProps={{ name: "wing_id" }}
                  >
                    <MenuItem value="" sx={menuItemSx}>
                      — Project Level (all wings)
                    </MenuItem>
                    {allWings.map((w) => (
                      <MenuItem
                        key={w.wingId?.wingId}
                        value={w.wingId?.wingId}
                        sx={menuItemSx}
                      >
                        {w.wingId?.wingName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormField>
              </Box>
              <Box className={cssClasses.additionalInputContainer} flex="100% !important">
                <FormField label="Show Allotment Letter Tab When" hint='"always" shows the tab for all units. Other options hide it based on farvisionStatus.'>
                  <Select
                    fullWidth
                    size="small"
                    name="show_condition"
                    value={template.show_condition}
                    onChange={onChange}
                    sx={selectSx}
                    inputProps={{ name: "show_condition" }}
                  >
                    <MenuItem value="always" sx={menuItemSx}>Always show</MenuItem>
                    <MenuItem value="status:Available" sx={menuItemSx}>Only when farvisionStatus = "Available"</MenuItem>
                    <MenuItem value="not:HOLD" sx={menuItemSx}>When farvisionStatus ≠ "HOLD"</MenuItem>
                    <MenuItem value="not:Hold" sx={menuItemSx}>When farvisionStatus ≠ "Hold"</MenuItem>
                  </Select>
                </FormField>
              </Box>
            </Box>
          </Box>

          {/* ── Letter Content ── */}
          <Box sx={sectionCardSx}>
            <Typography sx={sectionTitleSx}>Letter Content</Typography>
            <FormField label="Subject Line *" hint="Full subject text — no variables needed">
              <TextField
                fullWidth
                size="small"
                name="subject"
                value={template.subject}
                onChange={onChange}
                required
                sx={inputSx}
              />
            </FormField>
            <FormField label="Section 1 — Allotment Paragraph" hint={VARIABLES_HINT}>
              <TextField
                fullWidth
                multiline
                rows={5}
                name="section1_paragraph"
                value={template.section1_paragraph}
                onChange={onChange}
                sx={{ ...inputSx, "& .MuiInputBase-input": { padding: "1rem 1.2rem", fontSize: "1.4rem", fontFamily: "Inter-Regular", color: "#344054" } }}
              />
            </FormField>
          </Box>

          {/* ── Parking ── */}
          <Box sx={sectionCardSx}>
            <Typography sx={sectionTitleSx}>Parking</Typography>
            <FormControlLabel
              control={
                <Checkbox
                  name="has_parking_section"
                  checked={template.has_parking_section}
                  onChange={onChange}
                  sx={{ "& .MuiSvgIcon-root": { fontSize: "2rem" } }}
                />
              }
              label={
                <Typography sx={{ fontFamily: "Inter-Regular", fontSize: "1.4rem", color: "#344054" }}>
                  Include Section 2 — Allotment of Garage / Covered Parking Space(s)
                </Typography>
              }
            />
            <Typography sx={{ ...hintSx, marginLeft: "3.2rem" }}>
              When checked, parking details are shown and all subsequent section numbers increase by 1.
            </Typography>
          </Box>

          {/* ── Receipt ── */}
          <Box sx={sectionCardSx}>
            <Typography sx={sectionTitleSx}>Receipt</Typography>
            <FormField label="Receipt Paragraph" hint={`${VARIABLES_HINT}\nFor PNRD-style, write "Refer Cost Sheet" literally in the text.`}>
              <TextField
                fullWidth
                multiline
                rows={5}
                name="receipt_paragraph"
                value={template.receipt_paragraph}
                onChange={onChange}
                sx={inputSx}
              />
            </FormField>
          </Box>

          {/* ── Possession ── */}
          <Box sx={sectionCardSx}>
            <Typography sx={sectionTitleSx}>Possession</Typography>
            <FormField label="Possession Date *" hint='e.g. "31/12/2028"'>
              <TextField
                fullWidth
                size="small"
                name="possession_date"
                value={template.possession_date}
                onChange={onChange}
                required
                sx={inputSx}
              />
            </FormField>
            <FormControlLabel
              control={
                <Checkbox
                  name="possession_includes_parking"
                  checked={template.possession_includes_parking}
                  onChange={onChange}
                  sx={{ "& .MuiSvgIcon-root": { fontSize: "2rem" } }}
                />
              }
              label={
                <Typography sx={{ fontFamily: "Inter-Regular", fontSize: "1.4rem", color: "#344054" }}>
                  Add "along with covered car parking spaces(s)" to the possession clause
                </Typography>
              }
            />
          </Box>

          {/* ── Annexure A ── */}
          <Box sx={sectionCardSx}>
            <Typography sx={sectionTitleSx}>Annexure A</Typography>
            <FormControlLabel
              control={
                <Checkbox
                  name="has_annexure_a"
                  checked={template.has_annexure_a}
                  onChange={onChange}
                  sx={{ "& .MuiSvgIcon-root": { fontSize: "2rem" } }}
                />
              }
              label={
                <Typography sx={{ fontFamily: "Inter-Regular", fontSize: "1.4rem", color: "#344054" }}>
                  Include Annexure A — Stage wise Schedule of Completion (20-row table on a new page)
                </Typography>
              }
            />
            <Typography sx={{ ...hintSx, marginLeft: "3.2rem" }}>
              Required by RERA for most projects. PNRD does not have it.
            </Typography>
          </Box>

          {/* ── Signatory Block ── */}
          <Box sx={sectionCardSx}>
            <Typography sx={sectionTitleSx}>Signatory Block</Typography>
            <FormControlLabel
              control={
                <Checkbox
                  name="signatory_enabled"
                  checked={template.signatory_enabled}
                  onChange={onChange}
                  sx={{ "& .MuiSvgIcon-root": { fontSize: "2rem" } }}
                />
              }
              label={
                <Typography sx={{ fontFamily: "Inter-Regular", fontSize: "1.4rem", color: "#344054" }}>
                  Show Authorized Signatory block
                </Typography>
              }
            />
            <Typography sx={{ ...hintSx, marginLeft: "3.2rem" }}>
              PNRD (Wings A/B/C) does not show a signatory.
            </Typography>

            {template.signatory_enabled && (
              <Box className={cssClasses.FlexRow22} mt={2}>
                <Box className={cssClasses.additionalInputContainer}>
                  <FormField label="Company (FOR …)">
                    <TextField
                      fullWidth
                      size="small"
                      name="signatory_company"
                      value={template.signatory_company || ""}
                      onChange={onChange}
                      placeholder="e.g. FOR VILAS JAVDEKAR ECO HOMES"
                      sx={inputSx}
                    />
                  </FormField>
                </Box>
                <Box className={cssClasses.additionalInputContainer}>
                  <FormField label="Signatory Name">
                    <TextField
                      fullWidth
                      size="small"
                      name="signatory_name"
                      value={template.signatory_name || ""}
                      onChange={onChange}
                      placeholder="e.g. Mr. Vaibhav Kothamire"
                      sx={inputSx}
                    />
                  </FormField>
                </Box>
                <Box className={cssClasses.additionalInputContainer}>
                  <FormField label="Signatory Email">
                    <TextField
                      fullWidth
                      size="small"
                      name="signatory_email"
                      value={template.signatory_email || ""}
                      onChange={onChange}
                      placeholder="e.g. vaibhav.kothamire@javdekars.com"
                      sx={inputSx}
                    />
                  </FormField>
                </Box>
              </Box>
            )}
          </Box>

          {/* ── Applicant Block Layout ── */}
          <Box sx={sectionCardSx}>
            <Typography sx={sectionTitleSx}>Applicant Block Layout</Typography>
            <Box className={cssClasses.FlexRow22}>
              <Box className={cssClasses.additionalInputContainer} flex="100% !important">
                <FormField label="Layout" hint="Controls how applicant details appear at the top of the letter.">
                  <Select
                    fullWidth
                    size="small"
                    name="applicant_layout"
                    value={template.applicant_layout}
                    onChange={onChange}
                    sx={selectSx}
                    inputProps={{ name: "applicant_layout" }}
                  >
                    <MenuItem value="standard" sx={menuItemSx}>Standard — shows co-applicant PAN + mobile (PPSBR, PKC)</MenuItem>
                    <MenuItem value="pnrd" sx={menuItemSx}>PNRD — co-applicant name only, no PAN/mobile (PNRD Wings A/B/C)</MenuItem>
                    <MenuItem value="wingd" sx={menuItemSx}>Wing D — Aadhaar + Email on right, no co-applicant block (PNRD Wing D)</MenuItem>
                  </Select>
                </FormField>
              </Box>
            </Box>
            <FormControlLabel
              control={
                <Checkbox
                  name="acknowledgement_show_date"
                  checked={template.acknowledgement_show_date}
                  onChange={onChange}
                  sx={{ "& .MuiSvgIcon-root": { fontSize: "2rem" } }}
                />
              }
              label={
                <Typography sx={{ fontFamily: "Inter-Regular", fontSize: "1.4rem", color: "#344054" }}>
                  Show "Date: ___" on the acknowledgement signature line
                </Typography>
              }
            />
            <Typography sx={{ ...hintSx, marginLeft: "3.2rem" }}>
              PNRD (Wings A/B/C) does not show the date line.
            </Typography>
          </Box>

          {/* ── Footer Buttons ── */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: "1.2rem",
              marginTop: "2.4rem",
              paddingTop: "1.6rem",
              borderTop: "1px solid #EAECF0",
            }}
          >
            <Button
              style={{
                fontSize: "1.4rem",
                lineHeight: "2rem",
                color: "#344054",
                backgroundColor: "#FFFFFF",
                borderRadius: "0.8rem",
                padding: "0.8rem 2rem",
                border: "1px solid #D0D5DD",
              }}
              btnName="Cancel"
              clicked={onCancel}
            />
            <Button
              style={{
                fontSize: "1.4rem",
                lineHeight: "2rem",
                color: "#FFF",
                backgroundColor: saving ? "#84ADFF" : "#155EEF",
                borderRadius: "0.8rem",
                padding: "0.8rem 2.4rem",
              }}
              btnName={saving ? "Saving…" : "Save Template"}
              clicked={() => {}}
              disable={saving}
            />
          </Box>
        </Box>
      </Box>
    </Fragment>
  );
}

function FormField({ label, hint, children }) {
  return (
    <Box className={cssClasses.inputWrapper}>
      <Typography sx={labelSx}>{label}</Typography>
      {children}
      {hint && <Typography sx={hintSx}>{hint}</Typography>}
    </Box>
  );
}
